import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Avatar,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Collapse,
  Chip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  BusinessCenter,
  School,
  Refresh,
  ExpandMore,
  ExpandLess,
  Sort,
  AutoAwesome,
  PersonSearch,
  CheckCircle,
  ManageSearch,
  Leaderboard,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { getAllWithoutLimit, getJobsForCandidat, rerankJobsForCandidat } from '../../../services/CandidatService';
import { AppButton } from '../../../components/UI';
import LancerRecherchePopover from '../../Matching/components/LancerRecherchePopover';
import RerankPopover from '../../Matching/components/RerankPopover';


const panelSx = { display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: '14px', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(15,23,42,0.05)', overflow: 'hidden' };

const MATCH_MODES = [
  { value: 'hybride', label: 'Hybride' },
  { value: 'vecteur', label: 'Vectoriel' },
  { value: 'keywords', label: 'Mots-clés' },
];

const MEDALS = ['#f59e0b', '#94a3b8', '#b45309'];

const DetailTitle = ({ children }) => (
  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1 }}>
    {children}
  </Typography>
);

// Guided empty state: where the user is in the 3-step flow.
const Steps = ({ current }) => {
  const steps = [
    { icon: PersonSearch, title: 'Choisir un candidat', text: 'Recherchez le candidat par son nom.' },
    { icon: ManageSearch, title: 'Lancer la recherche IA', text: 'Le mode hybride donne en général les meilleurs résultats.' },
    { icon: Leaderboard, title: 'Comparer les offres', text: 'Classées par score, avec le détail par critère.' },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 380, mx: 'auto', width: '100%' }}>
      {steps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = s.icon;
        return (
          <Box key={s.title} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: '12px',
            border: `1.5px solid ${active ? '#03407b' : done ? '#86efac' : '#e2e8f0'}`,
            background: active ? '#f0f7fd' : done ? '#f0fdf4' : '#fff',
            opacity: i > current ? 0.6 : 1,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: done ? '#dcfce7' : active ? '#03407b' : '#f1f5f9', color: done ? '#16a34a' : active ? '#fff' : '#94a3b8',
            }}>
              {done ? <CheckCircle sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>{i + 1}. {s.title}</Typography>
              <Typography sx={{ fontSize: '0.76rem', color: '#64748b' }}>{s.text}</Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};


const StatCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  border: '1px solid rgba(17, 122, 167, 0.12)',
  borderRadius: 16,
  padding: theme.spacing(2),
  minHeight: 80,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2),
  boxShadow: 'none',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
  }
}));

const SelectedCandidateCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 16,
  border: '1px solid #4caf50',
  backgroundColor: '#f1fcf2',
  marginBottom: theme.spacing(2),
  boxShadow: 'none',
}));

const ActionCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  width: '100%',
}));

const OfferCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  border: selected ? '2px solid #117aa7' : '1px solid rgba(17, 122, 167, 0.15)',
  backgroundColor: selected ? 'rgba(17, 122, 167, 0.02)' : '#ffffff',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(17, 122, 167, 0.06)',
    borderColor: selected ? '#117aa7' : 'rgba(17, 122, 167, 0.4)',
  },
  marginBottom: theme.spacing(2),
}));

const LabelChip = styled(Box)(({ theme, variant }) => {
  const isCustom = variant === 'custom';
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isCustom ? '#edf7ed' : '#eef6ff',
    color: isCustom ? '#1e4620' : '#117aa7',
    borderRadius: 8,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  };
});

const SectionTitle = styled(Typography)(() => ({
  color: '#0d3e5d',
  fontWeight: 700,
  fontSize: '1rem',
  marginBottom: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));


const ScoreRing = ({ value, size = 64, showLabel = false }) => {
  const progress = Math.max(0, Math.min(100, value ?? 0));
  const color = progress >= 60 ? '#0f9d58' : progress >= 40 ? '#f4b400' : '#db4437';

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{ color: '#e2e8f0', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={progress}
          size={size}
          thickness={4}
          sx={{ color: color }}
        />
        <Box
          sx={{
            top: 0, left: 0, bottom: 0, right: 0,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant={size > 70 ? 'h6' : 'subtitle2'} sx={{ fontWeight: 700, color: '#0d3e5d', lineHeight: 1 }}>
            {progress}%
          </Typography>
          {showLabel && size > 70 && (
            <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', textAlign: 'center', mt: 0.5, lineHeight: 1 }}>
              score global
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};


const scoreColor = (v) => (v >= 60 ? '#0f9d58' : v >= 40 ? '#f4b400' : '#db4437');

/**
 * Maps the backend job-for-candidat response to the UI structure.
 * The API returns a list of jobs in data.jobs or data (array).
 */
const mapJobsToOffers = (apiData) => {
  // Gère les différentes formes possibles de la réponse
  const jobs = Array.isArray(apiData)
    ? apiData
    : Array.isArray(apiData?.jobs)
      ? apiData.jobs
      : Array.isArray(apiData?.missions)
        ? apiData.missions
        : Array.isArray(apiData?.results)
          ? apiData.results
          : Array.isArray(apiData?.data)
            ? apiData.data
            : [];

  return jobs.map((job, idx) => {
    const props = job.properties || {};
    const indScores = job.individual_scores || {};

    // Helper : convertit un score 0–1 en pourcentage entier
    const toPercent = (val, fallback = 0) => {
      const n = parseFloat(val);
      if (isNaN(n)) return fallback;
      // Si la valeur est déjà > 1, on suppose qu'elle est déjà en %
      return Math.round(n > 1 ? n : n * 100);
    };

    const buildDim = (key, label) => {
      const v = toPercent(indScores[key]);
      return { value: v, label, color: scoreColor(v) };
    };

    // Score global (0–1 → %)
    const score = toPercent(job.score ?? job.global_score ?? 0);

    const technicalSkills = Array.isArray(props.technical_skills) ? props.technical_skills : [];
    const programmingLanguages = Array.isArray(props.programming_languages) ? props.programming_languages : [];
    const spokenLanguages = Array.isArray(props.spoken_languages) ? props.spoken_languages : [];

    // Toutes les compétences à afficher = tech_skills + programming_languages
    const competences = [...technicalSkills, ...programmingLanguages];

    // Plan de formation (optionnel)
    const trainingPlan = job.training_plan || job.trainingPlan || {
      gaps: job.gaps || [],
      formations: job.formations || [],
    };

    return {
      id: job.uuid || job.id || idx,
      uuid: job.uuid || null,
      title: props.title || job.title || `Offre #${idx + 1}`,
      entreprise: props.company || job.company || '',
      industry: props.industry || '',
      score,
      experienceRequired: props.education_requirements || props.experience_level || props.years_of_experience_required || '',
      employmentType: props.employment_type || '',
      location: props.location || '',
      summary: props.summary || '',
      dimensionScores: {
        core_skills_score: buildDim('core_skills_score', 'Compétences'),
        language_score: buildDim('language_score', 'Langages'),
        semantic_score: buildDim('semantic_score', 'Sémantique'),
        role_score: buildDim('role_score', 'Rôle'),
        seniority_score: buildDim('seniority_score', 'Séniorité'),
      },
      competences: competences.slice(0, 8),
      technicalSkills,
      programmingLanguages,
      spokenLanguages,
      certifications: Array.isArray(props.certifications) ? props.certifications : [],
      description: props.job_description || job.description || '',
      trainingPlan: {
        gaps: Array.isArray(trainingPlan.gaps) ? trainingPlan.gaps : [],
        formations: Array.isArray(trainingPlan.formations) ? trainingPlan.formations : [],
      },
    };
  });
};


const MatchingCandidates = ({ snackbarShowMessage = () => { } }) => {
  const history = useHistory();
  const [candidateOptions, setCandidateOptions] = useState([]);
  const [candidateInputValue, setCandidateInputValue] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [loadingRerank, setLoadingRerank] = useState(false);
  const [offersError, setOffersError] = useState(null);
  const [trainingPlanOpen, setTrainingPlanOpen] = useState({});

  const [contractType, setContractType] = useState('Hybride');
  const [matchMode, setMatchMode] = useState('vecteur');
  const [limit, setLimit] = useState(10);
  const [shortlistLimit, setShortlistLimit] = useState(30);
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [rerankAnchor, setRerankAnchor] = useState(null);
  // The popovers use 'texte' for keyword search; this API calls it 'keywords'.
  const uiMode = matchMode === 'keywords' ? 'texte' : matchMode;
  const setUiMode = (m) => setMatchMode(m === 'texte' ? 'keywords' : m);

  const debounceRef = useRef(null);

  const fetchCandidates = useCallback(async (search = '') => {
    setLoadingCandidates(true);
    try {
      // L'API limite les résultats et filtre côté serveur pour éviter de charger toute la base.
      const { data } = await getAllWithoutLimit(search);
      // Extraction sécurisée : évite que [] (falsy) soit ignoré dans un || chain
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.candidats)
          ? data.candidats
          : Array.isArray(data?.data)
            ? data.data
            : [];
      // Filtrage côté client sur le terme de recherche
      const filtered = search.trim()
        ? list.filter((c) => {
          const fullName = `${c.prenom || ''} ${c.nom || ''}`.toLowerCase();
          return fullName.includes(search.toLowerCase());
        })
        : list;
      setCandidateOptions(filtered);
    } catch (err) {
      console.error('Erreur chargement candidats', err);
      setCandidateOptions([]);
    } finally {
      setLoadingCandidates(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates('');
  }, [fetchCandidates]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCandidates(candidateInputValue);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [candidateInputValue, fetchCandidates]);

  useEffect(() => {
    if (offers.length > 0) {
      setSelectedOffer(offers[0]);
      setTrainingPlanOpen({ [offers[0].id]: true });
    } else {
      setSelectedOffer(null);
      setTrainingPlanOpen({});
    }
  }, [offers]);

  const handleSearch = useCallback(async () => {
    if (!selectedCandidate) {
      snackbarShowMessage('Veuillez sélectionner un candidat.', 'warning');
      return;
    }
    const uuid = selectedCandidate.uuid || selectedCandidate.id;

    if (!uuid) {
      snackbarShowMessage('UUID du candidat introuvable.', 'error');
      return;
    }

    setLoadingOffers(true);
    setOffersError(null);
    setOffers([]);

    try {
      const response = await getJobsForCandidat(uuid, matchMode, limit);

      const { data } = response;
      const mapped = mapJobsToOffers(data);

      if (mapped.length === 0) {
        snackbarShowMessage('Aucune offre trouvée pour ce candidat.', 'info');
      }
      setOffers(mapped);
    } catch (err) {
      let errMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Erreur inconnue';
      const candidateName = selectedCandidate ? `${selectedCandidate.prenom || ''} ${selectedCandidate.nom || ''}`.trim() : null;
      if (typeof errMsg === 'string' && candidateName && (errMsg.toLowerCase().includes('no candidate found') || errMsg.toLowerCase().includes('no job found'))) {
        errMsg = `Candidat introuvable sous le nom "${candidateName}"`;
      }
      setOffersError(`Erreur : ${errMsg}. Vérifiez que le service IA est disponible.`);
    } finally {
      setLoadingOffers(false);
    }
  }, [selectedCandidate, matchMode, limit, shortlistLimit, snackbarShowMessage]);

  const handleRerank = useCallback(async () => {
    if (!selectedCandidate) {
      snackbarShowMessage('Veuillez sélectionner un candidat.', 'warning');
      return;
    }
    const uuid = selectedCandidate.uuid || selectedCandidate.id;

    if (!uuid) {
      snackbarShowMessage('UUID du candidat introuvable.', 'error');
      return;
    }

    setLoadingRerank(true);
    setOffersError(null);
    setOffers([]);

    try {
      const response = await rerankJobsForCandidat(uuid, matchMode, limit, shortlistLimit);
      const { data } = response;
      const mapped = mapJobsToOffers(data);

      if (mapped.length === 0) {
        snackbarShowMessage('Aucune offre trouvée après rerank.', 'info');
      } else {
      }
      setOffers(mapped);
    } catch (err) {
      let errMsg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Erreur inconnue';
      const candidateName = selectedCandidate ? `${selectedCandidate.prenom || ''} ${selectedCandidate.nom || ''}`.trim() : null;
      if (typeof errMsg === 'string' && candidateName && (errMsg.toLowerCase().includes('no candidate found') || errMsg.toLowerCase().includes('no job found'))) {
        errMsg = `Candidat introuvable sous le nom "${candidateName}"`;
      }
      setOffersError(`Erreur rerank : ${errMsg}. Vérifiez que le service IA est disponible.`);
    } finally {
      setLoadingRerank(false);
    }
  }, [selectedCandidate, matchMode, limit, shortlistLimit, snackbarShowMessage]);

  const toggleTrainingPlan = (offerId, e) => {
    e.stopPropagation();
    setTrainingPlanOpen((prev) => ({ ...prev, [offerId]: !prev[offerId] }));
  };

  const stats = useMemo(() => ({
    candidats: candidateOptions.length,
    offres: offers.length,
    entreprises: new Set(offers.map((o) => o.entreprise).filter(Boolean)).size,
    niveaux: new Set(offers.map((o) => o.experienceRequired).filter(Boolean)).size,
  }), [candidateOptions, offers]);

  const getCandidateLabel = (c) => `${c.prenom || ''} ${c.nom || ''}`.trim();

  const getInitials = (c) => {
    const p = (c?.prenom || '')[0] || '';
    const n = (c?.nom || '')[0] || '';
    return `${p}${n}`.toUpperCase();
  };

  const getCandidateCompetences = (c) => {
    if (!c) return [];
    return c.competences
      || c.skills
      || c.technologies
      || [];
  };

  const busy = loadingOffers || loadingRerank;
  const step = !selectedCandidate ? 0 : offers.length === 0 ? 1 : 2;
  const resetAll = () => { setOffers([]); setSelectedCandidate(null); setCandidateInputValue(''); setOffersError(null); };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: '#03407b' }}>
            <PersonSearch />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Trouver des offres pour un candidat</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>L'IA classe les missions actives selon leur adéquation avec le profil du candidat</Typography>
          </Box>
        </Box>

        {selectedCandidate && (
          <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '10px', background: '#f0f7fd', border: '1px solid #aed6f1', maxWidth: 420 }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#03407b', color: '#fff' }}>{getInitials(selectedCandidate)}</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#03407b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getCandidateLabel(selectedCandidate)}
              </Typography>
              {selectedCandidate.poste && <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{selectedCandidate.poste}</Typography>}
            </Box>
            {offers.length > 0 && (
              <Box sx={{ ml: 1, px: 1, py: 0.25, borderRadius: '999px', background: '#03407b', color: '#fff', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {offers.length} offre{offers.length > 1 ? 's' : ''}
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 5fr) 7fr' }, gap: 2, flex: 1, minHeight: 0 }}>

        <Box sx={{ ...panelSx, p: 1.75, gap: 1.25 }}>
          <Autocomplete
            options={candidateOptions}
            getOptionLabel={(option) => getCandidateLabel(option)}
            value={selectedCandidate}
            inputValue={candidateInputValue}
            onInputChange={(_, val) => setCandidateInputValue(val)}
            onChange={(_, newVal) => {
              setSelectedCandidate(newVal);
              setOffers([]);
              setOffersError(null);
            }}
            isOptionEqualToValue={(option, value) =>
              (option.id && value.id && option.id === value.id) ||
              (option.uuid && value.uuid && option.uuid === value.uuid)
            }
            loading={loadingCandidates}
            noOptionsText="Aucun candidat trouvé"
            loadingText="Chargement des candidats…"
            filterOptions={(x) => x}
            renderOption={({ key, ...props }, option) => (
              <Box component="li" {...props} key={option.uuid || option.id} sx={{ display: 'flex', gap: 1.25 }}>
                <Avatar sx={{ bgcolor: '#ebf4fb', color: '#03407b', width: 30, height: 30, fontSize: 12, fontWeight: 700 }}>
                  {getInitials(option)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>{getCandidateLabel(option)}</Typography>
                  {option.poste && <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{option.poste}</Typography>}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Rechercher un candidat…"
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', background: '#fff', minHeight: 40 } }}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps?.input,
                    startAdornment: (
                      <>
                        <InputAdornment position="start"><Search sx={{ color: '#94a3b8', fontSize: 20 }} /></InputAdornment>
                        {params.slotProps?.input?.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {loadingCandidates ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.slotProps?.input?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <AppButton variant="primary" size="small" startIcon={<AutoAwesome fontSize="small" />}
              onClick={(e) => setSearchAnchor(e.currentTarget)} disabled={!selectedCandidate || busy} loading={loadingOffers} sx={{ flex: 1 }}>
              {loadingOffers ? 'Recherche en cours…' : offers.length ? 'Relancer la recherche' : 'Lancer la recherche'}
            </AppButton>
            <Tooltip title="Ré-analyse en profondeur les meilleures offres pour affiner le classement">
              <span style={{ display: 'flex' }}>
                <AppButton variant="secondary" size="small" startIcon={<Sort fontSize="small" />}
                  onClick={(e) => setRerankAnchor(e.currentTarget)} disabled={!selectedCandidate || busy} loading={loadingRerank}>
                  {loadingRerank ? 'Classement…' : 'Affiner le classement'}
                </AppButton>
              </span>
            </Tooltip>
            <Tooltip title="Tout réinitialiser">
              <span style={{ display: 'flex' }}>
                <IconButton size="small" onClick={resetAll} disabled={busy || (!selectedCandidate && !offers.length)}
                  sx={{ width: 36, height: 36, borderRadius: '10px', border: '1px solid #e2e8f0', color: '#64748b', '&:hover': { background: '#f8fafc' } }}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <LancerRecherchePopover
            anchorEl={searchAnchor} onClose={() => setSearchAnchor(null)}
            filter={uiMode} setFilter={setUiMode} limit={limit} setLimit={setLimit}
            limitOptions={[5, 10, 20, 30]} itemLabel="offres" loading={loadingOffers}
            onLancer={() => { setSearchAnchor(null); handleSearch(); }}
          />
          <RerankPopover
            anchorEl={rerankAnchor} onClose={() => setRerankAnchor(null)}
            filter={uiMode} setFilter={setUiMode}
            shortlistLimit={shortlistLimit} setShortlistLimit={setShortlistLimit}
            limit={limit} setLimit={setLimit}
            limitOptions={[5, 10, 20, 30]} itemLabel="offres" rerankLoading={loadingRerank}
            onRerank={() => { setRerankAnchor(null); handleRerank(); }}
          />

          {offersError && (
            <Alert severity="error" onClose={() => setOffersError(null)} sx={{ borderRadius: '10px' }}>{offersError}</Alert>
          )}

          {offers.length > 0 && !busy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 700 }}>
                {offers.length} offre{offers.length > 1 ? 's' : ''} trouvée{offers.length > 1 ? 's' : ''}
              </Typography>
              <Chip label={`Mode ${MATCH_MODES.find((m) => m.value === matchMode)?.label || matchMode}`} size="small"
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: '#03407b', background: '#ebf4fb' }} />
              <Typography sx={{ ml: 'auto', fontSize: '0.72rem', color: '#94a3b8' }}>Triées par score</Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, minHeight: 0 }}>
            {busy ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#03407b', fontSize: '0.82rem', fontWeight: 600, mb: 0.5 }}>
                  <AutoAwesome sx={{ fontSize: 16 }} /> {loadingRerank ? "L'IA affine le classement des offres…" : "L'IA analyse les offres actives…"}
                </Box>
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={128} sx={{ borderRadius: '12px' }} />)}
              </Box>
            ) : offers.length === 0 ? (
              !offersError && (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 2 }}>
                  <Steps current={step} />
                </Box>
              )
            ) : (
              offers.map((offer, index) => {
                const isSelected = selectedOffer?.id === offer.id;
                const isPlanOpen = !!trainingPlanOpen[offer.id];
                const hasPlan = offer.trainingPlan.gaps.length > 0 || offer.trainingPlan.formations.length > 0;
                const medal = MEDALS[index];
                return (
                  <Box
                    key={offer.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedOffer(offer)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedOffer(offer)}
                    sx={{
                      mb: 1, p: 1.5, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.15s ease',
                      border: `1.5px solid ${isSelected ? '#03407b' : '#e2e8f0'}`,
                      background: isSelected ? '#f0f7fd' : '#fff',
                      '&:hover': { borderColor: isSelected ? '#03407b' : '#aed6f1', boxShadow: '0 4px 14px rgba(3,64,123,0.08)' },
                      '&:focus-visible': { outline: '2px solid #aed6f1', outlineOffset: 2 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{
                        width: 26, height: 26, borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.72rem', fontWeight: 800, background: medal || '#f1f5f9', color: medal ? '#fff' : '#64748b',
                      }}>
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {offer.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.76rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[offer.entreprise, offer.experienceRequired].filter(Boolean).join(' • ')}
                        </Typography>
                      </Box>
                      <ScoreRing value={offer.score} size={44} />
                    </Box>

                    {Object.keys(offer.dimensionScores || {}).length > 0 && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.5, mt: 1.25 }}>
                        {Object.entries(offer.dimensionScores).map(([dimKey, dim]) => dim && (
                          <Box key={dimKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ width: 76, fontSize: '0.68rem', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{dim.label}</Typography>
                            <LinearProgress variant="determinate" value={dim.value}
                              sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: dim.color } }} />
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, minWidth: 28, textAlign: 'right', color: '#334155' }}>{dim.value}%</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {offer.competences.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {offer.competences.slice(0, 5).map((comp, i) => (
                          <Chip key={i} label={comp} size="small" sx={{ height: 20, fontSize: '0.68rem', color: '#475569', background: '#f1f5f9' }} />
                        ))}
                        {offer.competences.length > 5 && (
                          <Chip label={`+${offer.competences.length - 5}`} size="small" sx={{ height: 20, fontSize: '0.68rem', color: '#03407b', background: '#ebf4fb', fontWeight: 700 }} />
                        )}
                      </Box>
                    )}

                    {hasPlan && (
                      <Box sx={{ borderTop: '1px solid #f1f5f9', pt: 1, mt: 1.25 }}>
                        <Button size="small" onClick={(e) => toggleTrainingPlan(offer.id, e)}
                          startIcon={<School sx={{ color: '#b45309' }} />} endIcon={isPlanOpen ? <ExpandLess /> : <ExpandMore />}
                          sx={{ textTransform: 'none', color: '#334155', fontWeight: 700, fontSize: '0.78rem', p: 0, '&:hover': { background: 'none' } }}>
                          Plan de formation ({offer.trainingPlan.gaps.length} écart{offer.trainingPlan.gaps.length > 1 ? 's' : ''})
                        </Button>
                        <Collapse in={isPlanOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px' }}>
                            {offer.trainingPlan.gaps.length > 0 && (
                              <>
                                <Typography sx={{ fontWeight: 800, color: '#b45309', mb: 0.5, fontSize: '0.68rem', textTransform: 'uppercase' }}>Écarts prioritaires</Typography>
                                <Box component="ol" sx={{ pl: 2, m: 0, mb: 1.25, fontSize: '0.78rem', color: '#451a03' }}>
                                  {offer.trainingPlan.gaps.map((gap, i) => <Box component="li" key={i} sx={{ mb: 0.25 }}>{gap}</Box>)}
                                </Box>
                              </>
                            )}
                            {offer.trainingPlan.formations.length > 0 && (
                              <>
                                <Typography sx={{ fontWeight: 800, color: '#b45309', mb: 0.5, fontSize: '0.68rem', textTransform: 'uppercase' }}>Formations recommandées</Typography>
                                <Box component="ol" sx={{ pl: 2, m: 0, fontSize: '0.78rem', color: '#451a03' }}>
                                  {offer.trainingPlan.formations.map((f, i) => (
                                    <Box component="li" key={i} sx={{ mb: 0.5 }}>
                                      <strong>{f.title} : </strong>{f.name}{f.source && ` (${f.source})`}
                                    </Box>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Box sx={panelSx}>
          {selectedOffer ? (
            <Box sx={{ p: 2.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.25 }}>{selectedOffer.title}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, mt: 0.25 }}>{selectedOffer.entreprise}</Typography>
                  {selectedOffer.experienceRequired && (
                    <Chip label={selectedOffer.experienceRequired} size="small" sx={{ mt: 1, height: 22, bgcolor: '#fffbeb', color: '#b45309', fontWeight: 700, border: '1px solid #fde68a' }} />
                  )}
                </Box>
                <ScoreRing value={selectedOffer.score} size={80} showLabel />
              </Box>

              <AppButton variant="primary" startIcon={<AutoAwesome fontSize="small" />}
                onClick={() => {
                  const cid = selectedCandidate.uuid || selectedCandidate.id;
                  history.push(`/candidats/${cid}/job-fit/${selectedOffer.id}`);
                }}>
                Analyser la compatibilité en détail
              </AppButton>

              <Box>
                <DetailTitle>Scores par critère</DetailTitle>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {Object.values(selectedOffer.dimensionScores || {}).filter(Boolean).map((dim) => (
                    <Box key={dim.label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>{dim.label}</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: dim.color }}>{dim.value}%</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={dim.value}
                        sx={{ height: 8, borderRadius: 4, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: dim.color } }} />
                    </Box>
                  ))}
                </Box>
              </Box>

              {selectedOffer.technicalSkills?.length > 0 && (
                <Box>
                  <DetailTitle>Compétences requises</DetailTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedOffer.technicalSkills.map((skill, i) => (
                      <Chip key={i} label={skill} size="small" sx={{ height: 22, bgcolor: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedOffer.spokenLanguages?.length > 0 && (
                <Box>
                  <DetailTitle>Langues requises</DetailTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedOffer.spokenLanguages.map((lang, i) => (
                      <Chip key={i} label={lang} size="small" sx={{ height: 22, bgcolor: '#f5f3ff', color: '#5b21b6', fontWeight: 600, fontSize: '0.72rem' }} />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedOffer.description && (
                <Box>
                  <DetailTitle>Description du poste</DetailTitle>
                  <Box
                    sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.85rem', '& p': { m: 0, mb: 1 } }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedOffer.description) }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4, textAlign: 'center', gap: 1 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f7fd', color: '#7fb3d3', mb: 1 }}>
                <BusinessCenter sx={{ fontSize: 36 }} />
              </Box>
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: '#334155' }}>
                {offers.length > 0 ? 'Sélectionnez une offre' : "Le détail de l'offre apparaîtra ici"}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8', maxWidth: 360 }}>
                {offers.length > 0
                  ? "Cliquez sur une offre de la liste pour voir ses scores détaillés, les compétences requises et la description du poste."
                  : 'Choisissez un candidat puis lancez la recherche pour obtenir le classement des offres.'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MatchingCandidates;
